import React, { useEffect, useMemo, useState } from "react";
import PolicyRulesDataGrid from "../DataGrids/PolicyRulesDataGrid";
import ActionsFragment from "../Fragments/ActionsFragment";
import UseCasesActionsFragment from "../Fragments/UseCasesActionsFragment";
import CoveredAgeFragment from "../Fragments/CoveredAgeFragment";
import RadioGenderFragment from "../Fragments/RadioGenderFragment";
import StartDateFragment from "../Fragments/StartDateFragment";
import { IconButton, Box, Button } from "@mui/material";
import { AddCircle, Tornado } from "@mui/icons-material";
import { DEMOGRAPHICS_RFG } from "../Constants/PolicyTranslationConstants";
import ProcedureCodeDialog from "../../PolicyTranslation/Dialogs/ProcedureCodeDialog";
import DxLimitationSelectFragment from "../Fragments/DxLimitationSelectFragment";
import DuplicateRowFragment from "../Fragments/DuplicateRowFragment";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import GenderAgeMeetCC from "../Fragments/GenderAgeMeetCC";
import { getLobName, getAgeRange } from "../Utils/TranslationUtils";
import { renderCellClass, renderError } from "../Validation/ValidationHelper";
import { showVarianceIcon } from "utils/shared";
import StyledTornadoIcon from "components/TornadoIcon";

const AddProcedureCode = styled("a")(({ theme }) => {
  return {
    textTransform: "uppercase" /* Make text uppercase */,
    color: "blue" /* Standard hyperlink color */,
    pointerEvents: "none" /* Make the link not clickable */,
    cursor: "default" /* Change cursor to default */,
  };
});

const DemographicsTable = ({
  gridHeight,
  setLoading,
  updateRulesAfterEdit,
  availableProcedureCodes,
  chips,
  editable,
  rows,
  setSelectedCC,
  addNewRows,
  duplicateRow,
  selectedCC,
  handleDelete,
  policyData,
  pageComponent,
  dxLimitationValues,
  handleRemoveInvalidRow,
  endExistingRule,
}) => {
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const [openDxDialog, setOpenDxDialog] = useState(false);
  const [dxSelectedValue, setDxSelectedValue] = useState({});
  const { state } = useLocation();

  const selectedHealthPlan = state?.policyState?.healthPlanName;
  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const handleAddRowToGrid = (checkedItems) => {
    addNewRows(checkedItems, DEMOGRAPHICS_RFG);
    setOpenDxDialog(false);
  };

  const columns = [
    {
      display: "flex",
      field: "CoverageCriteriaList",
      headerName: "CC#",
      width: 50,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      valueGetter: (value, row) => {
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.CoverageCriteriaList = value ? value?.trim() : "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.CoverageCriteriaList?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "procedureCode",
      headerName: "Procedure Code",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DuplicateRowFragment
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            value={params.row.RuleFacts.ProcedureCode}
            duplicateRow={duplicateRow}
          />
          {editable &&
            showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
              <StyledTornadoIcon />
            )}
        </Box>
      ),
      valueGetter: (value, row) => row.RuleFacts.ProcedureCode,
    },

    {
      display: "flex",
      field: "dxLimitation",
      headerName: "Dx Limitation",
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <DxLimitationSelectFragment
          updateRulesAfterEdit={updateRulesAfterEdit}
          editable={editable && pageComponent === "PolicyTranslation"}
          params={params}
          options={dxLimitationValues}
        />
      ),
    },
    {
      display: "flex",
      field: "coveredGender",
      headerName: "Gender",
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      minWidth: 190,
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
      renderEditCell: (params) => {
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
    },
    {
      display: "flex",
      field: "genderAgeMeetsCC",
      headerName: "Gender & Age Meets CC?",
      width: 260,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderEditCell: (params) => {
        return (
          <GenderAgeMeetCC
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <GenderAgeMeetCC
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
          />
        );
      },
    },
    {
      display: "flex",
      field: "ageLow",
      headerName: "Age Low",
      align: "center",
      headerAlign: "center",
      cellClassName: (params) => renderCellClass(params),

      flex: 0.4,
      minWidth: 140,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.ageLow?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.DisplayAgeRange = `${value},${row.RuleFacts.ageHigh}`;
        newRow.RuleFacts.ageLow = value?.trim() || "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.ageLow?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "ageHigh",
      headerName: "Age High",
      align: "center",
      headerAlign: "center",
      cellClassName: (params) => renderCellClass(params),

      flex: 0.4,
      minWidth: 140,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.ageHigh?.trim() || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.DisplayAgeRange = `${row.RuleFacts.ageLow},${value}`;
        newRow.RuleFacts.ageHigh = value?.trim() || "";
        return newRow;
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return row.RuleFacts.ageHigh?.trim() || "";
      },
    },
    {
      display: "flex",
      field: "startDate",
      headerName: "Date & LOB Configuration",
      flex: 1.1,
      minWidth: 220,
      cellClassName: (params) => renderCellClass(params),
      editable: editable,
      renderEditCell: (params) => {
        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
            endExistingRule={endExistingRule}
          />
        );
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }

        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
          />
        );
      },
    },
    {
      display: "flex",
      field: "LobDisplayName",
      headerName: "LOB",
      minWidth: 150,
      valueGetter: (value, row) => {
        return getLobName(row.RuleFacts.LobConfigKey, policyData);
      },
      cellClassName: (params) => renderCellClass(params),

      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value || "";
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return pageComponent === "PolicyTranslation" ? (
          <ActionsFragment
            handleDelete={handleDelete}
            editable={editable}
            params={params}
          />
        ) : (
          <UseCasesActionsFragment
            editable={editable && pageComponent === "UseCases"}
            params={params}
          />
        );
      },
    },
  ];

  const columnGroupingModel = [
    {
      groupId: "Age Range",
      children: [{ field: "ageLow" }, { field: "ageHigh" }],
    },
  ];

  const sanitizedRows = useMemo(() => {
    return rows.map((row) => {
      const sanitizedRow = { ...row };
      const ageRange = getAgeRange(
        row?.RuleFacts?.DisplayAgeRange,
        row?.RuleFacts?.InvalidAgeRange,
        row?.RuleFacts?.DisplayAgeGenderRule
      );
      sanitizedRow.RuleFacts = { ...row.RuleFacts };

      sanitizedRow.RuleFacts.ageHigh = ageRange[1];
      sanitizedRow.RuleFacts.ageLow = ageRange[0];
      return sanitizedRow;
    });
  }, [rows]);

  return (
    <div style={{ width: "100%" }}>
      {pageComponent !== "UseCases" && (
        <Box>
          <IconButton
            disabled={!editable}
            onClick={handleAddProcedureCode}
            color="primary"
            aria-label="add new row"
          >
            <AddCircle />
          </IconButton>
          <AddProcedureCode>Add Procedure Code</AddProcedureCode>
        </Box>
      )}
      <PolicyRulesDataGrid
        gridHeight={gridHeight}
        setLoading={setLoading}
        columnGroupingModel={columnGroupingModel}
        editable={editable && pageComponent === "PolicyTranslation"}
        rows={sanitizedRows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "procedureCode", sort: "asc" }],
          },
        }}
        processRowUpdate={(newRow, oldRow) =>
          updateRulesAfterEdit(newRow, oldRow)
        }
      />
      <ProcedureCodeDialog
        availableProcedureCodes={availableProcedureCodes}
        selectedCC={selectedCC}
        setSelectedCC={setSelectedCC}
        chips={chips}
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddRowToGrid}
      />
    </div>
  );
};

export default DemographicsTable;
